import { useEffect, useRef, useState } from 'react';
import { Loader2, Plus, ShieldCheck, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { t } from '@/i18n';
import { useUsersApi, type UserAdminListItem, type ModuleGrantInput } from '@/services/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TagBadge } from '@/components/shared/TagBadge';
import { PERMISSION_MODULES, USER_ROLE_NAMES } from '@ip/shared-types';
import type { CompanyListItem, PermissionModule, Tag, UserRoleName } from '@ip/shared-types';

/** Mirrors UsersAdminService.TAG_FILTERABLE_MODULES on the backend. */
const TAG_FILTERABLE_MODULES = ['expense', 'sales'] as const;
export type TagFilterableModule = (typeof TAG_FILTERABLE_MODULES)[number];

function isTagFilterableModule(module: PermissionModule): module is TagFilterableModule {
  return (TAG_FILTERABLE_MODULES as readonly string[]).includes(module);
}

interface GrantState {
  modules: Set<PermissionModule>;
  tagsByModule: Partial<Record<TagFilterableModule, string[]>>;
}

function grantStateFromUser(user: UserAdminListItem, companyNip: string): GrantState {
  const grantsForCompany = user.grants.filter((g) => g.companyNip === companyNip);
  const modules = new Set<PermissionModule>(grantsForCompany.map((g) => g.module));
  const tagsByModule: Partial<Record<TagFilterableModule, string[]>> = {};
  for (const module of TAG_FILTERABLE_MODULES) {
    const tagIds = grantsForCompany
      .filter((g) => g.module === module && g.tagId)
      .map((g) => g.tagId as string);
    if (tagIds.length > 0) tagsByModule[module] = tagIds;
  }
  return { modules, tagsByModule };
}

interface CompanyAccessCardProps {
  user: UserAdminListItem;
  company: CompanyListItem;
  /** Tags for this company's expense/sales modules — undefined until lazily loaded by the parent. */
  tags?: Partial<Record<TagFilterableModule, Tag[]>>;
  /** Ask the parent to fetch this company's tags (parent deduplicates). */
  onNeedTags: () => void;
  /** Parent reloads the user list — this card receives a fresh `user` prop as a result. */
  onChanged: () => void;
}

/**
 * One company's access block in the permissions drawer: role badges with an
 * add-role menu, and a module editor that only renders when it has an effect
 * (module grants matter for the 'user' role — admin/ksiegowy see everything
 * regardless, pracownik/hr are fixed to the HR module).
 *
 * Module and tag toggles auto-save: each change immediately PUTs the full
 * grant set. While a request is in flight further clicks only update local
 * state; the latest snapshot is re-sent once, so rapid toggling never sends
 * stale or out-of-order sets. On failure the state reverts to the server's.
 */
export function CompanyAccessCard({ user, company, tags, onNeedTags, onChanged }: CompanyAccessCardProps) {
  const { addRole, removeRole, replaceGrants } = useUsersApi();

  // Lazy initializer + key={company.nip} on the caller side: state resets per
  // company, and survives the user-prop refreshes that follow each save.
  const [grant, setGrant] = useState<GrantState>(() => grantStateFromUser(user, company.nip));
  const latestGrant = useRef(grant);
  const saveInFlight = useRef(false);
  const savePending = useRef(false);
  const [saving, setSaving] = useState(false);
  const [addingRole, setAddingRole] = useState(false);
  const [removingRoleId, setRemovingRoleId] = useState<string | null>(null);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [removingAccess, setRemovingAccess] = useState(false);

  const rolesForCompany = user.roles.filter((r) => r.companyNip === company.nip);
  const availableRoles = USER_ROLE_NAMES.filter(
    (role) => !rolesForCompany.some((r) => r.role === role),
  );

  const hasFullAccess = rolesForCompany.some((r) => r.role === 'admin' || r.role === 'ksiegowy');
  const hasUserRole = rolesForCompany.some((r) => r.role === 'user');
  // Grants without a 'user' role are dormant leftovers — still show the
  // editor so an admin can see and clean them up.
  const showModuleEditor = !hasFullAccess && (hasUserRole || grant.modules.size > 0);

  const needsTags =
    showModuleEditor && TAG_FILTERABLE_MODULES.some((module) => grant.modules.has(module));
  useEffect(() => {
    if (needsTags && !tags) onNeedTags();
  }, [needsTags, tags, onNeedTags]);

  /** Sends the latest grant snapshot; coalesces clicks made while a request is in flight. */
  const persistGrants = async () => {
    if (saveInFlight.current) {
      savePending.current = true;
      return;
    }
    saveInFlight.current = true;
    setSaving(true);
    let succeeded = true;
    do {
      savePending.current = false;
      const snapshot = latestGrant.current;
      const modules: ModuleGrantInput[] = Array.from(snapshot.modules).map((module) => ({
        module,
        tagIds:
          isTagFilterableModule(module) && (snapshot.tagsByModule[module]?.length ?? 0) > 0
            ? snapshot.tagsByModule[module]
            : undefined,
      }));
      try {
        await replaceGrants(user.id, { companyNip: company.nip, modules });
      } catch (error) {
        toast.error(error instanceof Error ? error.message : t('users.errors.saveGrantsFailed'));
        console.error(error);
        // Revert to the last state the server confirmed (the user prop).
        const reverted = grantStateFromUser(user, company.nip);
        latestGrant.current = reverted;
        setGrant(reverted);
        savePending.current = false;
        succeeded = false;
      }
    } while (savePending.current);
    saveInFlight.current = false;
    setSaving(false);
    if (succeeded) onChanged();
  };

  const applyGrant = (next: GrantState) => {
    latestGrant.current = next;
    setGrant(next);
    void persistGrants();
  };

  const toggleModule = (module: PermissionModule) => {
    const modules = new Set(grant.modules);
    if (!modules.delete(module)) modules.add(module);
    applyGrant({ ...grant, modules });
  };

  const toggleTag = (module: TagFilterableModule, tagId: string) => {
    const existing = grant.tagsByModule[module] ?? [];
    const next = existing.includes(tagId)
      ? existing.filter((id) => id !== tagId)
      : [...existing, tagId];
    applyGrant({ ...grant, tagsByModule: { ...grant.tagsByModule, [module]: next } });
  };

  const handleAddRole = async (role: UserRoleName) => {
    try {
      setAddingRole(true);
      await addRole(user.id, { role, companyNip: company.nip });
      toast.success(t('users.drawer.roleAdded'));
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('users.errors.addRoleFailed'));
      console.error(error);
    } finally {
      setAddingRole(false);
    }
  };

  const handleRemoveRole = async (roleId: string) => {
    try {
      setRemovingRoleId(roleId);
      await removeRole(user.id, roleId);
      toast.success(t('users.drawer.roleRemoved'));
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('users.errors.removeRoleFailed'));
      console.error(error);
    } finally {
      setRemovingRoleId(null);
    }
  };

  const handleRemoveAccess = async () => {
    try {
      setRemovingAccess(true);
      for (const role of rolesForCompany) {
        await removeRole(user.id, role.id);
      }
      await replaceGrants(user.id, { companyNip: company.nip, modules: [] });
      toast.success(t('users.drawer.accessRemoved'));
      setConfirmRemoveOpen(false);
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('users.errors.removeAccessFailed'));
      console.error(error);
    } finally {
      setRemovingAccess(false);
    }
  };

  return (
    <div className="space-y-3 rounded-lg border p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{company.name || company.nip}</p>
          <p className="text-muted-foreground font-mono text-xs">NIP {company.nip}</p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-destructive size-7 shrink-0"
          aria-label={t('users.drawer.removeAccess')}
          onClick={() => setConfirmRemoveOpen(true)}
        >
          <Trash2 className="size-3.5" />
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {rolesForCompany.map((role) => (
          <Badge key={role.id} variant="secondary" className="gap-1">
            {t(`users.roleNames.${role.role}`)}
            <button
              type="button"
              aria-label={t('common.delete')}
              disabled={removingRoleId === role.id}
              onClick={() => handleRemoveRole(role.id)}
              className="opacity-70 hover:opacity-100 disabled:opacity-40"
            >
              {removingRoleId === role.id ? (
                <Loader2 className="size-3 animate-spin" />
              ) : (
                <X className="size-3" />
              )}
            </button>
          </Badge>
        ))}
        {availableRoles.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-6 gap-1 px-2 text-xs"
                disabled={addingRole}
              >
                {addingRole ? <Loader2 className="size-3 animate-spin" /> : <Plus className="size-3" />}
                {t('users.drawer.addRole')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {availableRoles.map((role) => (
                <DropdownMenuItem key={role} onSelect={() => handleAddRole(role)}>
                  {t(`users.roleNames.${role}`)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {hasFullAccess ? (
        <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
          <ShieldCheck className="size-3.5 shrink-0" />
          {t('users.drawer.fullAccess')}
        </p>
      ) : showModuleEditor ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label className="text-muted-foreground text-xs">{t('users.drawer.grants')}</Label>
            {saving && <Loader2 className="text-muted-foreground size-3 animate-spin" />}
          </div>
          {!hasUserRole && (
            <p className="text-muted-foreground text-xs">{t('users.drawer.grantsOnlyForUserRole')}</p>
          )}
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {PERMISSION_MODULES.map((module) => (
              <label key={module} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={grant.modules.has(module)}
                  onCheckedChange={() => toggleModule(module)}
                />
                {t(`users.moduleNames.${module}`)}
              </label>
            ))}
          </div>

          {TAG_FILTERABLE_MODULES.filter((module) => grant.modules.has(module)).map((module) => (
            <div key={module} className="space-y-1">
              <Label className="text-muted-foreground text-xs">
                {t('users.form.tagsForModule', { module: t(`users.moduleNames.${module}`) })}
              </Label>
              <div className="flex flex-wrap gap-1.5">
                {!tags ? (
                  <Loader2 className="text-muted-foreground size-3.5 animate-spin" />
                ) : (tags[module] ?? []).length === 0 ? (
                  <p className="text-muted-foreground text-xs">{t('users.form.noTagsForModule')}</p>
                ) : (
                  tags[module]?.map((tag) => (
                    <TagBadge
                      key={tag.id}
                      name={tag.name}
                      color={tag.color}
                      selected={(grant.tagsByModule[module] ?? []).includes(tag.id)}
                      onClick={() => toggleTag(module, tag.id)}
                    />
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
          <ShieldCheck className="size-3.5 shrink-0" />
          {t('users.drawer.hrOnlyAccess')}
        </p>
      )}

      <AlertDialog open={confirmRemoveOpen} onOpenChange={(o) => !o && setConfirmRemoveOpen(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('users.drawer.removeAccessConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('users.drawer.removeAccessConfirmMessage', { company: company.name || company.nip })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removingAccess}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={removingAccess}
              onClick={(e) => {
                e.preventDefault();
                handleRemoveAccess();
              }}
            >
              {removingAccess && <Loader2 className="size-4 animate-spin" />}
              {t('users.drawer.removeAccess')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
